import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, Clock, PlugZap, CalendarRange, Users, ChevronRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { QuickAddHomework } from "@/components/QuickAddHomework";
import { NowNextBanner } from "@/components/NowNext";
import { ukToday, plusDays } from "@/lib/subjects";
import { fmtTime } from "@/lib/sourceMeta";
import { BriefView } from "@/components/BriefView";
import { TonightPlan } from "@/components/TonightPlan";
import { PackingChecklist } from "@/components/PackingChecklist";
import { YearGroupPrompt } from "@/components/YearGroup";
import { StreakBadge } from "@/components/StreakBadge";
import { useEdition } from "@/lib/edition";

const Shortcut = ({ to, icon: I, title, text, id }) => (
  <Link to={to} data-testid={id} className="card card-hover flex items-center gap-4 p-4 sm:p-5">
    <span className="grid place-items-center w-11 h-11 shrink-0 rounded-2xl bg-gold-400/15 text-gold-300"><I className="w-5 h-5" /></span>
    <span className="flex-1 min-w-0"><span className="block font-semibold text-white">{title}</span><span className="block text-xs text-slate-500">{text}</span></span>
    <ChevronRight className="w-5 h-5 text-slate-500" />
  </Link>
);

const greeting = () => {
  const h = Number(new Date().toLocaleString("en-GB", { timeZone: "Europe/London", hour: "2-digit", hour12: false }));
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

export default function Dashboard() {
  const { w } = useEdition();
  const [digest, setDigest] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);
  const { user } = useAuth();
  const [liveTT, setLiveTT] = useState(null);
  const [liveHW, setLiveHW] = useState(null);
  const loadHW = useCallback(() => {
    api.get("/homework").then((r) => {
      const open = (Array.isArray(r.data) ? r.data : []).filter((h) => !h.done);
      const t = ukToday(), tm = plusDays(1);
      const hw = open.filter((h) => h.kind !== "test");
      setLiveHW({ overdue: hw.filter((h) => h.due < t), today: hw.filter((h) => h.due === t), tomorrow: hw.filter((h) => h.due === tm),
        tests: open.filter((h) => h.kind === "test" && h.due >= t && h.due <= plusDays(14)) });
    }).catch(() => {});
    api.get("/revision/today").then((r) => setLiveRev(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const [liveRev, setLiveRev] = useState([]);
  const tickRevision = async (s) => { await api.put(`/homework/${s.hw_id}/plan/${s.idx}`, { done: !s.done }).catch(() => {}); loadHW(); };
  const firstName = (user?.name || "there").split(" ")[0];

  const poll = useCallback((id) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/digests/${id}`);
        setDigest(data);
        if (data?.status === "running") poll(id);
        else if (data?.status === "done") toast.success("Your brief is ready");
        else toast.error(data?.error ?? "Summary failed");
      } catch { poll(id); }
    }, 2500);
  }, []);

  useEffect(() => {
    Promise.all([api.get("/digests/latest"), api.get("/status")])
      .then(([d, s]) => { setDigest(d.data?.digest_id ? d.data : null); setStatus(s.data ?? null); if (d.data?.status === "running") poll(d.data.digest_id); })
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/timetable").then((r) => setLiveTT(r.data?.today && (r.data?.weeks?.A || r.data?.weeks?.B) ? r.data : null)).catch(() => {});
    loadHW();
    return () => clearTimeout(timer.current);
  }, [poll, loadHW]);

  const run = async () => {
    try {
      const { data } = await api.post("/digests/run");
      setDigest(data);
      poll(data.digest_id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const running = digest?.status === "running";
  const off = status?.brief_day?.brief_on === false;

  return (
    <main data-testid="dashboard-page" className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12 space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <p className="eyebrow">{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight">{greeting()}, <span className="italic text-gold-300">{firstName}</span>.</h1>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            {off ? (
              <span data-testid="day-off-badge" className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-900 border border-amber-200 px-3 py-1"><Clock className="w-3.5 h-3.5" /> {status.brief_day.reason || "Day off"} · next brief {fmtTime(status?.next_run)}</span>
            ) : (
              <>
                <span data-testid="next-run-badge" className="inline-flex items-center gap-1.5 rounded-full bg-teal-50 text-teal-800 border border-teal-200 px-3 py-1"><Clock className="w-3.5 h-3.5" /> Auto-brief on {w("school days", "workdays")} 07:00 UK · next {fmtTime(status?.next_run)}</span>
                <span data-testid="last-run-badge" className="inline-flex items-center rounded-full bg-surface border border-slate-200 text-slate-600 px-3 py-1">Last brief {fmtTime(digest?.created_at)}</span>
              </>
            )}
          </div>
        </div>
        {!off && <button data-testid="summarise-now-button" onClick={run} disabled={running}
          className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition active:scale-[0.98] ${running ? "bg-slate-100 text-gold-300 cursor-wait" : "bg-brand text-white hover:bg-brand-dark shadow-red"}`}>
          {running ? <><Loader2 className="w-4 h-4 animate-spin" /> {w("Planning your school day…", "Planning your workday…")}</> : <><Sparkles className="w-4 h-4" /> Summarise now</>}
        </button>}
      </div>
      <StreakBadge />

      {liveTT && !liveTT.evening && <NowNextBanner today={liveTT.today} />}
      <YearGroupPrompt />
      <QuickAddHomework onAdded={loadHW} />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-start">
        <TonightPlan key={`tonight-${liveHW ? JSON.stringify(liveHW).length : 0}`} />
        <div className="space-y-4">
          <PackingChecklist />
          <Shortcut to="/week" icon={CalendarRange} id="dash-week-link" title="Your week" text={w("Lessons, homework and tests Monday to Friday", "Meetings, tasks and deadlines Monday to Friday")} />
          <Shortcut to="/groups" icon={Users} id="dash-groups-link" title={w("Study groups", "Teams")} text={w("Chat, notes and quizzes with friends", "Chat, notes and shared boards with colleagues")} />
        </div>
      </div>

      {!loading && status && !status.has_timetable && (
        <Link to="/connections" data-testid="connect-prompt" className="flex items-center gap-4 rounded-2xl border border-dashed border-teal-400 bg-teal-50/60 p-5 hover:bg-teal-50 transition-colors">
          <PlugZap className="w-6 h-6 text-teal-700 shrink-0" />
          <span className="text-sm sm:text-base text-slate-700"><b className="text-slate-900">{w("Add your timetable.", "Add your work schedule.")}</b> {w("Upload a screenshot of your school timetable so Daybreyk can plan your day and your kit.", "Import your calendar or add your regular meetings so Daybreyk can plan your day.")}</span>
        </Link>
      )}

      {loading ? <div className="h-64 rounded-3xl bg-surface/70 animate-pulse" /> : off ? null :
        digest?.status === "done" ? <BriefView onRevisionTick={tickRevision} digest={{ ...digest, revision: liveRev, ...(liveTT ? { timetable: liveTT } : {}), ...(liveHW ? { homework: liveHW } : {}) }} /> :
        running ? (
          <div data-testid="brief-running" className="rounded-3xl bg-navy-900 text-white p-10 text-center">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-teal-300" />
            <p className="mt-4 font-display text-3xl">Claude is planning your day…</p>
            <p className="mt-2 text-slate-300 text-sm">Usually under a minute.</p>
          </div>
        ) : digest?.status === "error" ? (
          <div data-testid="brief-error" className="rounded-2xl bg-rose-50 border border-rose-200 p-6 text-rose-700">The last summary failed: {digest.error}. Try "Summarise now" again.</div>
        ) : (
          <div data-testid="brief-empty" className="rounded-3xl bg-surface border border-slate-200 p-10 text-center">
            <p className="font-display text-3xl text-slate-900">No brief yet.</p>
            <p className="mt-2 text-slate-600">Press "Summarise now" or wait for tomorrow's 07:00 brief.</p>
          </div>
        )}
    </main>
  );
}
